import { FC, useEffect, useMemo, useRef } from 'react'
import LazyHydrate from 'react-lazy-hydration'

import { Media } from '@syconium/little-miss-figgy'

import { RenderOnceInViewport } from '../../../../app/_components/performance/RenderOnceInViewport.client'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { usePortal } from '../../../../app/_providers/PortalProvider.client'
import {
  useTrackProductGroupImpression,
  useTrackProductImpression,
} from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { FitModelCaption } from '../../../../brunswick/components/fit-model/FitModelCaption'
import { StickyParent } from '../../../../brunswick/components/fit-model/FitModelCaption/styles'
import { NostoProductRecommendationsSection } from '../../../../brunswick/components/NostoProductRecommendationsSection'
import { ReviewSection, ReviewsBody } from '../../../../brunswick/components/Reviews'
import { SliderFitModelWrapper } from '../../../../brunswick/components/SwiperImageSlider/styles'
import { EmbroideryService } from '../../../../brunswick/containers/embroidery'
import { EmbroideryModalService } from '../../../../brunswick/containers/embroidery/EmbroideryModalService'
import { useFixturesContext } from '../../../../brunswick/containers/fixtures'
import { canonicalBaseUrls } from '../../../../constants/figs'
import { AccentColorContainer } from '../../../../containers/AccentColorContainer'
import { useGoogleTagManager } from '../../../../lib/hooks/useGoogleTagManager'
import { useProductReviews } from '../../../../lib/hooks/useProductReviews'
import {
  formatPrettyTitlePG,
  getProductGalleryImages,
  makeCanonicalProductUrl,
} from '../../../../lib/utils'
import { ProductAsset, Variant } from '../../../../types/graphql'
import { PageSection } from '../../../page-sections/PageSection'
import { ProductPageBreadcrumbs } from '../Breadcrumbs/ProductPageBreadcrumbs'
import * as ProductPageStyles from '../styles'

import { AddToBagContainer } from './contexts/AddToBagContainer'
import { FitModelStateContainer } from './contexts/FitModelStateContainer'
import { GalleryImageStateContainer } from './contexts/GalleryImageStateContainer'
import { ProductDetailsContainer } from './contexts/ProductDetailsContainer'
import { WaitlistService } from './contexts/WaitlistService'
import { DetailsSection } from './DetailsSection'
import { EmbroideryModal } from './embroidery/EmbroideryModal/EmbroideryModal'
import { FigsScrubsAreSection } from './FigsScrubsAreSection'
import { UseFetchProductPageDataOutput } from './hooks/useFetchProductPageData'
import { useIsGiftCard } from './hooks/useIsGiftCard'
import { usePortalEmbroiderySettings } from './hooks/usePortalEmbroiderySettings'
import { useProductPageEmbroiderySettings } from './hooks/useProductPageEmbroiderySettings'
import { useProductPagePortal } from './hooks/useProductPagePortal'
import { ImageZoom } from './ImageZoom'
import { ProductAssetsSection } from './ProductAssetsSection'
import { SchemaTags } from './SchemaTags'
import ShopTheLookSection from './ShopTheLookSection'
import { ReviewsPlaceholder } from './styles'
import { WaitlistModal } from './WaitlistModal'
import { WhyWeLove } from './WhyWeLove'

// We very specifically do not want to allow missing data to reach this component.
// So we enforce that anyone leveraging this UI component provides non-null data
// and that they handle idle/rejected states for us.
export interface ProductPageTemplateProps {
  productGroup: Exclude<UseFetchProductPageDataOutput['productGroup'], null | undefined>
  productGroupStatus: Exclude<
    UseFetchProductPageDataOutput['productGroupStatus'],
    'rejected' | 'idle'
  >
  featuredProduct: Exclude<UseFetchProductPageDataOutput['featuredProduct'], null | undefined>
  featuredProductStatus: Exclude<
    UseFetchProductPageDataOutput['featuredProductStatus'],
    'rejected' | 'idle'
  >
  selectedVariant: Variant | undefined
}

const MINIMUM_COLOR_COUNT = 3

const ProductPageSchemaTags: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct } = props

  const { productReviews } = useProductReviews({
    shopifyProductId: featuredProduct.shopifyId,
  })

  // DANGER & TODO: There is no loading state for fixtures... so we can have content flash. It
  // is not likely to happen since we prime fixtures in the PrimaryLayout component. But that
  // doesn't mean we shouldn't be coding to handle the case where this is loading. Especially
  // on other pages that don't use the PrimaryLayout

  const canonicalUrl = useMemo(() => {
    const currentColorKey = featuredProduct.colorInfo?.rawName
    const colors = productGroup.colorGroups.flatMap(o => o.colors)
    return productGroup && currentColorKey
      ? makeCanonicalProductUrl(
          canonicalBaseUrls.products,
          colors,
          productGroup.handle,
          currentColorKey
        )
      : `${canonicalBaseUrls.products}${productGroup.handle}`
  }, [featuredProduct.colorInfo?.rawName, productGroup])

  const variant = featuredProduct.variants?.[0]
  return (
    <LazyHydrate whenIdle>
      <>
        {variant && canonicalUrl && (
          <SchemaTags
            canonicalUrl={canonicalUrl}
            productGroup={productGroup}
            productReviews={productReviews}
            product={featuredProduct}
            variant={variant}
          />
        )}
      </>
    </LazyHydrate>
  )
}

const ProductPageProductGallery: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct } = props
  const { fitModel, setFitModel } = FitModelStateContainer.useContainer()

  const iconBadge = featuredProduct.iconBadge
  const iconBadgePlacement = featuredProduct.iconBadgePlacement ?? productGroup.iconBadgePlacement

  const previousFeaturedProductRef = useRef(featuredProduct)
  if (previousFeaturedProductRef.current !== featuredProduct) {
    previousFeaturedProductRef.current = featuredProduct
    setFitModel(featuredProduct.fitModel ?? undefined)
  }

  const images: readonly ProductAsset[] = useMemo(() => {
    if (!featuredProduct.images?.nodes) return []
    return getProductGalleryImages(featuredProduct.images.nodes)
  }, [featuredProduct])
  const videos = useMemo(() => {
    return featuredProduct?.videos.nodes.filter(
      video => video.kind === 'ugc' || video.kind === 'catwalk' || video.kind === 'other'
    )
  }, [featuredProduct?.videos.nodes])

  const catwalkVideoIndex = useMemo(() => {
    return videos.findIndex(video => {
      return video.kind === 'catwalk'
    })
  }, [videos])

  const existFitModelsToDisplay =
    fitModel && featuredProduct.fitModelCount && featuredProduct.fitModelCount > 1

  const displayOnlyModelSelector = !!(existFitModelsToDisplay && catwalkVideoIndex < 0)
  const colorCount = productGroup?.colorGroups.reduce((sum, group) => {
    return sum + group.colors?.length
  }, 0)
  const displayColorGallery = colorCount > MINIMUM_COLOR_COUNT
  const colorGalleryDecision = useFeatureFlag({
    key: 'color-gallery-flag',
    defaultVariant: 'color-gallery-control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  const fitModelCaption = useMemo(() => {
    if (displayOnlyModelSelector && colorCount <= MINIMUM_COLOR_COUNT) {
      return (
        <SliderFitModelWrapper>
          <StickyParent>
            <FitModelCaption
              activeFitModel={fitModel}
              colorCount={colorCount}
              activeProduct={featuredProduct}
              selectFitModel={x => setFitModel(x)}
              variant={'model-selector-only'}
              displayColorGallery={displayColorGallery}
            />
          </StickyParent>
        </SliderFitModelWrapper>
      )
    }

    return catwalkVideoIndex >= 0 || colorGalleryDecision ? (
      <FitModelCaption
        colorCount={colorCount}
        activeFitModel={fitModel}
        activeProduct={featuredProduct}
        selectFitModel={x => setFitModel(x)}
        variant={'catwalk-video'}
        displayColorGallery={displayColorGallery}
      />
    ) : null
  }, [
    featuredProduct,
    fitModel,
    setFitModel,
    displayOnlyModelSelector,
    catwalkVideoIndex,
    colorGalleryDecision,
    colorCount,
    displayColorGallery,
  ])

  return (
    <LazyHydrate whenVisible>
      <GalleryImageStateContainer.Provider
        initialState={{
          activeFitModel: fitModel,
          detailImages: featuredProduct.detailImages,
          images,
          videos,
        }}
      >
        <ImageZoom />
        <ProductAssetsSection
          fitModelCaption={fitModelCaption}
          iconBadge={iconBadge}
          iconBadgePlacement={iconBadgePlacement}
          title={productGroup.title}
          productGroupStyle={productGroup.style}
        />
        <Media lessThan='md'>
          {fitModel && displayOnlyModelSelector && (
            <FitModelCaption
              colorCount={colorCount}
              activeFitModel={fitModel}
              activeProduct={featuredProduct}
              selectFitModel={x => setFitModel(x)}
              variant={'model-selector-only'}
              displayColorGallery={displayColorGallery}
            />
          )}
        </Media>
      </GalleryImageStateContainer.Provider>
    </LazyHydrate>
  )
}

const ProductPageProductDetails: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct, selectedVariant } = props

  const prettyTitle: string = formatPrettyTitlePG(productGroup)

  return (
    <LazyHydrate whenVisible>
      <>
        {productGroup.handle !== 'figs-gift-card' && (
          <ReviewSection
            title={prettyTitle}
            description={productGroup.description}
            productHandle={featuredProduct.handle}
            shopifyProductId={props.featuredProduct.shopifyId}
          />
        )}
        <ProductDetailsContainer.Provider
          state={{ featuredProduct, productGroup, selectedVariant }}
        >
          <AddToBagContainer.Provider>
            <DetailsSection />
          </AddToBagContainer.Provider>
        </ProductDetailsContainer.Provider>
      </>
    </LazyHydrate>
  )
}

const ProductPageProductSection: FC<ProductPageTemplateProps> = props => {
  const {
    productGroup,
    productGroupStatus,
    featuredProduct,
    featuredProductStatus,
    selectedVariant,
  } = props
  const { hasMandatoryLogoEmbroidery } = usePortal()
  const prettyTitle: string = formatPrettyTitlePG(productGroup)
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery(productGroup.category)

  const currentColorKey = featuredProduct.colorInfo?.rawName ?? ''
  const currentProductColor = useMemo(() => {
    const colors = productGroup.colorGroups.flatMap(o => o.colors) ?? []
    return colors.find(o => o.rawName === currentColorKey)
  }, [currentColorKey, productGroup])

  // Portal-specific settings related to the current product/product group
  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup.portalColorGroups ?? [],
    selectedColorKey: currentColorKey,
  })

  // Embroidery settings related to the current product/product group
  const { embroideryOptions, embroideryVariantsByKind } = useProductPageEmbroiderySettings({
    embroidery: featuredProduct.embroidery,
    mandatoryLogoEmbroidery,
    isPortalColorSelected,
  })

  // Embroidery settings related to the user's current portal
  const {
    hiddenEmbroideryFields,
    defaultInitialFormValues,
    defaultSelectedEmbroideryOptions,
    textEmbroiderySecondLineOptions,
  } = usePortalEmbroiderySettings({
    embroideryOptions,
    mandatoryLogoEmbroidery,
    isPortalColorSelected,
  })

  const productImagesWithDefaultFlatlays: ProductAsset[] = useMemo(() => {
    return (featuredProduct.images?.nodes ?? []).concat(productGroup.embroideryImages ?? [])
  }, [featuredProduct, productGroup])

  return (
    <FitModelStateContainer.Provider
      initialState={{
        fitModel: featuredProduct.fitModel ?? undefined,
      }}
    >
      <WaitlistService.Provider
        initialState={{
          currentColorKey,
          productTitle: prettyTitle,
          selectedVariant,
        }}
      >
        <EmbroideryService.Provider
          initialState={{
            hideFields: hiddenEmbroideryFields,
            defaultInitialFormValues,
            defaultSelectedEmbroideryOptions,
            embroideryOptions,
            embroideryVariantsByKind,
            productImagesWithDefaultFlatlays,
            productColor: currentProductColor,
            productCategory: productGroup.category,
            selectedSize: selectedVariant ? selectedVariant.size : null,
            textEmbroideryOptions: {
              secondLine: textEmbroiderySecondLineOptions,
            },
          }}
          key={`${productGroup.handle}`}
        >
          <EmbroideryModalService.Provider initialState={{ isPortalColorSelected }}>
            <EmbroideryModal embroideryOptions={embroideryOptions} />

            <WaitlistModal />
            <ProductPageBreadcrumbs
              genderCategory={productGroup.genderCategory}
              category={productGroup.rawCategory}
            />
            <ProductPageStyles.PrimaryAndAsideSection
              fadeOut={productGroupStatus === 'pending' || featuredProductStatus === 'pending'}
            >
              <ProductPageStyles.PrimaryColumnDiv>
                <ProductPageProductGallery {...props} />
              </ProductPageStyles.PrimaryColumnDiv>

              <ProductPageStyles.AsideColumnDiv>
                <ProductPageProductDetails {...props} />
              </ProductPageStyles.AsideColumnDiv>
            </ProductPageStyles.PrimaryAndAsideSection>
          </EmbroideryModalService.Provider>
        </EmbroideryService.Provider>
      </WaitlistService.Provider>
    </FitModelStateContainer.Provider>
  )
}

const ProductPageContentfulSections: FC<ProductPageTemplateProps> = props => {
  const { productGroup } = props

  if (!productGroup.productGroupView) return null

  return (
    <LazyHydrate whenVisible>
      <>
        {productGroup.productGroupView.sections.map((productGroupViewSectionModel, index) => {
          return (
            <PageSection
              key={`${productGroupViewSectionModel.id}-${index}`}
              sectionModel={productGroupViewSectionModel}
              index={index + 1}
            />
          )
        })}
      </>
    </LazyHydrate>
  )
}

const ProductPageFigsScrubsAreSection: FC<ProductPageTemplateProps> = props => {
  const { productGroup } = props

  // DANGER & TODO: There is no loading state for fixtures... so we can have content flash. It
  // is not likely to happen since we prime fixtures in the PrimaryLayout component. But that
  // doesn't mean we shouldn't be coding to handle the case where this is loading. Especially
  // on other pages that don't use the PrimaryLayout
  const {
    pages: {
      product: { scrubCategories },
    },
  } = useTranslation()

  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle,
  })
  if (!isGiftCard && !scrubCategories.includes(productGroup.category)) return null

  return (
    <RenderOnceInViewport offset={{ bottom: 200 }}>
      <section>
        <FigsScrubsAreSection />
      </section>
    </RenderOnceInViewport>
  )
}

const ProductPageReviewsSection: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct } = props
  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle,
  })

  const { productReviews } = useProductReviews({
    shopifyProductId: props.featuredProduct.shopifyId,
  })

  if (isGiftCard) return null

  return (
    <ReviewsPlaceholder>
      <RenderOnceInViewport offset={{ bottom: 200 }}>
        <section>
          <ReviewsBody productReviews={productReviews} handle={featuredProduct.handle} />
        </section>
      </RenderOnceInViewport>
    </ReviewsPlaceholder>
  )
}

const ProductPageContentfulRecommendationsSection: FC<ProductPageTemplateProps> = props => {
  return (
    <RenderOnceInViewport offset={{ bottom: 200 }}>
      <section data-testid='shop-the-look'>
        <ShopTheLookSection productHandle={props.featuredProduct.handle} />
      </section>
    </RenderOnceInViewport>
  )
}

const ProductPageAutomatedRecommendationsSection: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct } = props

  // DANGER & TODO: There is no loading state for fixtures... so we can have content flash. It
  // is not likely to happen since we prime fixtures in the PrimaryLayout component. But that
  // doesn't mean we shouldn't be coding to handle the case where this is loading. Especially
  // on other pages that don't use the PrimaryLayout
  const {
    pages: {
      product: { productRecommendations },
    },
  } = useTranslation()

  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle,
  })
  const variant = featuredProduct.variants?.[0]

  if (!variant) return null
  if (isGiftCard) return null

  return (
    <RenderOnceInViewport offset={{ bottom: 200 }}>
      <div data-testid='nosto-product-recos'>
        <NostoProductRecommendationsSection
          analyticsContext={{ category: 'product-carousel-tile::productpage-nosto-1' }}
          nostoPlacementId='productpage-nosto-1'
          title={nostoTitle => nostoTitle ?? productRecommendations.title}
        />
      </div>
    </RenderOnceInViewport>
  )
}

const ProductPageWhyWeLoveSection: FC<ProductPageTemplateProps> = props => {
  const { productGroup, featuredProduct } = props
  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle,
  })
  const wwltImage = featuredProduct?.images?.nodes.find(image => image.kind === 'wwlt')

  if (isGiftCard) return null

  return (
    <LazyHydrate whenVisible>
      <Media greaterThanOrEqual='md'>
        <section>
          <WhyWeLove
            imageSrc={wwltImage?.source}
            whyWeLoveText={featuredProduct.whyWeLove}
            whyWeLoveIcons={featuredProduct.iconList}
          />
        </section>
      </Media>
    </LazyHydrate>
  )
}

// NavContainer re-renders A LOT!, so we are just going to interact with it in a dummy
// component. This protects other components from not having to rerender. The PDP emits
// changes to the accent color, so not sub component would own this logic either.
const ProductPageDataEmitter: FC<ProductPageTemplateProps> = props => {
  const { featuredProduct, selectedVariant, productGroup } = props
  const { enablePDPAccentColor } = useFixturesContext()
  const { setAccentColor } = AccentColorContainer.useContainer()
  const { pushProductClick, pushProductDetailsImpression } = useGoogleTagManager()
  const { trackProductImpression } = useTrackProductImpression()
  const { trackProductGroupImpression } = useTrackProductGroupImpression()

  const accentColor = enablePDPAccentColor
    ? featuredProduct.colorInfo?.accentColor ?? undefined
    : undefined

  useEffect(() => {
    setAccentColor(accentColor)
    return () => {
      setAccentColor(undefined)
    }
  }, [accentColor, setAccentColor])

  // This is technically not an accurate piece of code. A use effect based on `selectedVariant`
  // can technically fire without someone clicking something, if the shared URL includes
  // the size query param or if the product only has one size... so I think this should technically
  // be refactored and triggered on click of something maybe rather than via a useEffect :shrug:
  useEffect(() => {
    if (!selectedVariant) return
    const numericShopifyVariantIdMatch = selectedVariant?.shopifyId.match(/\d+$/)
    if (!numericShopifyVariantIdMatch) return
    pushProductClick({ variant: selectedVariant })
  }, [pushProductClick, selectedVariant])

  // As of now, this uses `variant` instead of `product`. But it fires only on product change
  // and it only uses the first variant of that product. So it basically makes no sense and
  // likely should be altered to make the impression based on the `featuredProduct` without
  // looking at the first variant at all.
  useEffect(() => {
    const variant = featuredProduct.variants?.[0]
    if (!variant) return
    pushProductDetailsImpression({ variant })

    trackProductImpression({
      handle: featuredProduct.handle,
      id: featuredProduct.id,
      shopifyId: featuredProduct.shopifyId,
      featuredProduct: true,
      soldOut: featuredProduct.soldOut,
      color: featuredProduct.colorInfo?.rawName ?? '',
      productGroupHandle: productGroup.handle,
      genderCategory: productGroup.genderCategory,
      variantInformation: featuredProduct.variants
        ? featuredProduct.variants.map(variant => ({
            size: variant.size ? variant.size : '',
            soldOut: variant.soldOut,
            discounted: variant.discountPrice != null,
          }))
        : [],
    })
  }, [featuredProduct, productGroup, pushProductDetailsImpression, trackProductImpression])

  useEffect(() => {
    trackProductGroupImpression({
      handle: productGroup.handle,
    })
  }, [trackProductGroupImpression, productGroup])

  return <></>
}

// Note: Don't access hooks in here. We don't want to add hook dependencies
// at such a high level that they render this entire page when their data changes.
// Really we want to avoid hooks pretty much at all in this file, and push them down
// towards leaf node components to minimize their impact on performance.
export const ProductPageTemplate: FC<ProductPageTemplateProps> = props => {
  // DANGER & TODO: We don't want to display the recommendations section for portal users on PDP's that contain an active portal product.
  // Considering we don't know if a user is part of a portal on the server, this component will rendered during SSR and then removed
  // on the client after we've identified the user is part of a portal & that portal-specific color groups were returned for the product.
  const showRecommendationSections = Boolean(
    !props.productGroup.portalColorGroups || props.productGroup.portalColorGroups.length === 0
  )

  return (
    <ProductPageStyles.Main>
      <ProductPageProductSection {...props} />
      <ProductPageContentfulSections {...props} />
      <ProductPageWhyWeLoveSection {...props} />
      {showRecommendationSections && (
        <>
          <ProductPageContentfulRecommendationsSection {...props} />
          <ProductPageAutomatedRecommendationsSection {...props} />
        </>
      )}
      <ProductPageReviewsSection {...props} />
      <ProductPageFigsScrubsAreSection {...props} />
      <ProductPageDataEmitter {...props} />
      <ProductPageSchemaTags {...props} />
    </ProductPageStyles.Main>
  )
}

export default ProductPageTemplate
